import React, { useState, useEffect } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import { Layout, Button, Typography, message, Space } from 'antd'
import Spinner from '../../components/Spinner'
import {
  DownloadOutlined,
  FilePdfOutlined,
  FileImageOutlined,
  FileOutlined,
  ArrowLeftOutlined
} from '@ant-design/icons'

import { generateLocalPreview } from '../../services/googleDrive'

const { Content } = Layout
const { Title, Text } = Typography

const FilePreview = () => {
  const location = useLocation()
  const history = useHistory()
  const [loading, setLoading] = useState(true)
  const [fileUrl, setFileUrl] = useState('')
  const [downloadUrl, setDownloadUrl] = useState('')
  const [fileName, setFileName] = useState('')
  const [fileType, setFileType] = useState('')
  const [isGDrive, setIsGDrive] = useState(false)

  useEffect(() => {
    const fetchPreview = async () => {
      const params = new URLSearchParams(location.search)
      let path = params.get('path')
      const provider = params.get('provider')

      if (!path) {
        message.error('File path not provided')
        history.goBack()
        return
      }

      // Google Drive detection
      const containsGDrivePattern = path.includes('drive.google.com') || path.includes('docs.google.com')
      const isExplicitGDrive = provider === 'gdrive'

      if (isExplicitGDrive || containsGDrivePattern) {
        setIsGDrive(true)
        const fileIdMatch = path.match(/[-\w]{25,}/)
        const fileId = fileIdMatch ? fileIdMatch[0] : path

        // Fetch metadata to enable Microsoft Viewer for PPTX files on Drive
        try {
          const { getGoogleFileInfo } = await import('../../services/googleDrive')
          const result = await getGoogleFileInfo(fileId)
          if (result.success) {
            setDownloadUrl(result.data.downloadUrl)
            const extension = result.data.name.split('.').pop()?.toLowerCase() || ''
            setFileType(extension)
            setFileName(result.data.name)
          }
        } catch (e) {
          console.warn('Metadata fetch failed')
        }

        setFileUrl(`https://drive.google.com/file/d/${fileId}/preview`)
        if (!fileName) setFileName('Google Drive Preview')
        if (!fileType) setFileType('gdrive')
        setLoading(false)
        return
      }

      // Local file handling
      setIsGDrive(false)
      const pathParts = path.split('/')
      const name = decodeURIComponent(pathParts[pathParts.length - 1])
      const extension = name.split('.').pop()?.toLowerCase() || ''

      const isOfficeDoc = ['pptx', 'ppt', 'docx', 'doc', 'xlsx', 'xls', 'pdf'].includes(extension)
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'

      // Special handling for Office docs and PDFs on Localhost - Proxy via Google Drive to get a public link
      if (isOfficeDoc && isLocalhost) {
        try {
          const result = await generateLocalPreview(path, name)
          setFileUrl(result.previewUrl)
          setDownloadUrl(result.downloadUrl)
          setFileName(name)
          setFileType(extension)
          setIsGDrive(true)
          setLoading(false)
          return
        } catch (error) {
          console.warn('Proxy failed:', error)
        }
      }

      setFileName(name)
      setFileType(extension)

      let fullUrl = path
      if (!path.startsWith('http')) {
        const serverUrl = process.env.REACT_APP_API_URL || 'https://wiseacademybackend.vercel.app'
        const normalizedPath = path.startsWith('/') ? path : `/${path}`
        fullUrl = `${serverUrl}${normalizedPath}`
      }
      setFileUrl(fullUrl)
      setDownloadUrl(fullUrl)
      setLoading(false)
    }

    fetchPreview()
  }, [location, history])

  const handleDownload = () => {
    if (isGDrive && fileUrl.includes('drive.google.com')) {
      window.open(fileUrl.replace('/preview', '/view'), '_blank')
    } else {
      const link = document.createElement('a')
      link.href = fileUrl
      link.download = fileName
      link.target = '_blank'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }

  const renderPreview = () => {
    if (loading) {
      <div style={{ textAlign: 'center', padding: '100px' }}>
        <Spinner size="large" />
        <p style={{ marginTop: 20, fontSize: '16px', color: '#666' }}>Initializing engine...</p>
      </div>
    }

    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'

    // Universal Document Viewer (White Background)
    const isDoc = ['pptx', 'ppt', 'docx', 'doc', 'xlsx', 'xls', 'pdf'].includes(fileType)
    const canUseExternalViewer = downloadUrl && !downloadUrl.includes('localhost')

    if (isDoc && canUseExternalViewer) {
      const viewerUrl = (fileType === 'pptx' || fileType === 'ppt')
        ? `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(downloadUrl)}`
        : `https://docs.google.com/gview?url=${encodeURIComponent(downloadUrl)}&embedded=true`

      return (
        <iframe
          src={viewerUrl}
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            background: '#fff',
            display: 'block'
          }}
          title={fileName}
        />
      )
    }

    switch (fileType) {
      case 'pdf':
        const canUseWhitePdfViewer = downloadUrl && !downloadUrl.includes('localhost')
        if (canUseWhitePdfViewer) {
          return (
            <iframe
              src={`https://docs.google.com/gview?url=${encodeURIComponent(downloadUrl)}&embedded=true`}
              style={{ width: '100%', height: '100%', border: 'none', background: '#fff' }}
              title={fileName}
            />
          )
        }

        // Local PDF fallback with minimal dark margins and hidden toolbar
        return (
          <div style={{ width: '100%', height: '100%', background: '#fff', padding: '20px', display: 'flex', justifyContent: 'center' }}>
            <iframe
              src={`${fileUrl}#toolbar=0&navpanes=0&scrollbar=1&view=FitH`}
              style={{
                width: '100%',
                maxWidth: '1000px',
                height: '100%',
                border: '1px solid #eee',
                background: '#fff',
                boxShadow: '0 10px 40px rgba(0,0,0,0.05)'
              }}
              title={fileName}
            />
          </div>
        )

      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'webp':
        return (
          <div style={{ textAlign: 'center', padding: '20px', minHeight: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <img src={fileUrl} alt={fileName} style={{ maxWidth: '100%', maxHeight: 'calc(100vh - 180px)', boxShadow: '0 4px 20px rgba(0,0,0,0.15)', objectFit: 'contain' }} />
          </div>
        )

      case 'mp4':
      case 'webm':
        return (
          <div style={{ textAlign: 'center', padding: '20px', background: '#000', minHeight: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <video controls style={{ maxWidth: '100%', maxHeight: 'calc(100vh - 180px)' }}>
              <source src={fileUrl} type={`video/${fileType}`} />
            </video>
          </div>
        )

      case 'docx':
      case 'doc':
      case 'xlsx':
      case 'xls':
        if (isLocalhost && !downloadUrl) {
          return (
            <div style={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '40px', background: '#fafafa' }}>
              <div style={{ background: '#fff', padding: '48px', borderRadius: '24px', textAlign: 'center', boxShadow: '0 10px 40px rgba(0,0,0,0.05)', maxWidth: '600px' }}>
                <FileOutlined style={{ fontSize: '72px', color: '#1890ff', marginBottom: '24px' }} />
                <Title level={3}>Local Preview Limitation</Title>
                <p style={{ marginBottom: '32px', color: '#666', fontSize: '16px' }}>
                  Online document viewers cannot reach files on your <strong>localhost</strong>.
                </p>
                <Button size="large" type="primary" icon={<DownloadOutlined />} onClick={handleDownload}>
                  Download {fileType.toUpperCase()}
                </Button>
              </div>
            </div>
          )
        }
        return (
          <iframe
            src={`https://docs.google.com/gview?url=${encodeURIComponent(downloadUrl || fileUrl)}&embedded=true`}
            style={{ width: '100%', height: '100%', border: 'none', background: '#fff' }}
            title={fileName}
          />
        )

      default:
        // Fallback for native Google Drive files (Slides, Docs, Sheets)
        if (isGDrive && fileUrl.includes('drive.google.com')) {
          return (
            <iframe
              src={fileUrl}
              style={{ width: '100%', height: '100%', border: 'none', background: '#fff' }}
              title="Drive Preview"
            />
          )
        }

        return (
          <div style={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '40px', background: '#fafafa' }}>
            <FileOutlined style={{ fontSize: '64px', color: '#8c8c8c', marginBottom: '20px' }} />
            <Title level={4}>Preview Not Available</Title>
            <Button type="primary" size="large" icon={<DownloadOutlined />} onClick={handleDownload}>
              Download {fileName}
            </Button>
          </div>
        )
    }
  }

  const getFileIcon = () => {
    switch (fileType) {
      case 'pdf': return <FilePdfOutlined style={{ color: '#f5222d' }} />
      case 'jpg': case 'jpeg': case 'png': return <FileImageOutlined style={{ color: '#52c41a' }} />
      default: return <FileOutlined />
    }
  }

  return (
    <Layout style={{ minHeight: '100vh', background: '#fff' }}>
      <div style={{
        height: '70px',
        padding: '0 24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid #f0f0f0',
        background: '#fff',
        zIndex: 10
      }}>
        <Space size="large">
          <Button icon={<ArrowLeftOutlined />} onClick={() => history.goBack()} type="text" style={{ fontSize: '16px' }}>
            Back
          </Button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '20px', display: 'flex' }}>{getFileIcon()}</span>
            <Title level={4} style={{ margin: 0, fontSize: '18px' }}>{fileName}</Title>
          </div>
        </Space>
        <Button type="primary" size="large" icon={<DownloadOutlined />} onClick={handleDownload} shape="round">
          Download
        </Button>
      </div>
      <Content style={{ height: 'calc(100vh - 70px)', overflow: 'hidden' }}>
        {renderPreview()}
      </Content>
    </Layout>
  )
}

export default FilePreview
